import React, { useState, useEffect, useCallback } from 'react';
import NavbarV2 from './components/v2/NavbarV2';
import HeroSectionV2 from './components/v2/HeroSectionV2';
import AboutSectionV2 from './components/v2/AboutSectionV2';
import ProjectsSectionV2 from './components/v2/ProjectsSectionV2';
import ContactSectionV2 from './components/v2/ContactSectionV2';
import FooterV2 from './components/v2/FooterV2';
import LoaderV2 from './components/v2/LoaderV2';
import CursorGlow from './components/v2/CursorGlow';
import ParticleField from './components/v2/ParticleField';
import OSApp from './components/os/OSApp';
import { KONAMI_SEQUENCE } from './constants/konami';
import './index.css';

const THEME_KEY = 'portfolio-theme';

// Assuming you have other assets you want to preload
const assets = [
  // For example, if you have specific background images or icons:
  // '/images/hero-bg.jpg',
  // '/icons/project-icon.png',
  // Add more image paths as needed, if any
];

const preloadImage = src =>
  new Promise(resolve => {
    const img = new window.Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = resolve; // Resolve even on error to not block loading
  });

const App = () => {
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [otherAssetsLoaded, setOtherAssetsLoaded] = useState(false);
  const [splineSceneLoaded, setSplineSceneLoaded] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'os';
    return window.localStorage.getItem(THEME_KEY) || 'os';
  });
  const [konamiUnlocked, setKonamiUnlocked] = useState(false);
  const konamiProgress = React.useRef(0);

  const isOS = theme === 'os';

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'v2' ? 'os' : 'v2'));
  }, []);

  useEffect(() => {
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Konami code easter egg — works regardless of active theme
  useEffect(() => {
    const handleKeyDown = e => {
      const expected = KONAMI_SEQUENCE[konamiProgress.current];
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key === expected) {
        konamiProgress.current += 1;
        if (konamiProgress.current === KONAMI_SEQUENCE.length) {
          konamiProgress.current = 0;
          setKonamiUnlocked(true);
          setTimeout(() => setKonamiUnlocked(false), 3200);
        }
      } else {
        konamiProgress.current = key === KONAMI_SEQUENCE[0] ? 1 : 0;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Effect to preload other assets (images, etc.)
  useEffect(() => {
    // If there are no other specific assets to preload, you can remove this useEffect
    // and set otherAssetsLoaded to true initially.
    if (assets.length > 0) {
      Promise.all(assets.map(preloadImage)).then(() => {
        setOtherAssetsLoaded(true);
      });
    } else {
      setOtherAssetsLoaded(true); // No other assets to load
    }
  }, []);

  // Handler for when the Spline scene reports it's loaded
  const handleSplineLoad = () => {
    setSplineSceneLoaded(true);
  };

  useEffect(() => {
    // Smooth linear progress from 0 to 100
    if (loading) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev < 99) { // Stop just before 100 to allow for final checks
            return prev + 1;
          } else {
            clearInterval(interval);
            return prev;
          }
        });
      }, 15); // Adjust speed here (lower = faster)
      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    // Only hide loader when progress is at 100 AND all assets (including Spline) are loaded
    if (progress >= 99 && otherAssetsLoaded && splineSceneLoaded) {
      // Ensure progress reaches 100
      setProgress(100);
      setTimeout(() => setLoading(false), 700); // Show 100% for a moment before hiding
    }
  }, [progress, otherAssetsLoaded, splineSceneLoaded]);

  const konamiToast = konamiUnlocked && (
    <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
      <div className="absolute inset-0 bg-ink/70 animate-pulse" />
      <div className="relative font-display text-2xl md:text-4xl text-legendary text-center px-8 py-6 border-2 border-legendary rounded-lg bg-panel shadow-[0_0_40px_rgba(245,166,35,0.6)]">
        🎮 Secret Achievement Unlocked
        <div className="font-mono text-sm md:text-base text-gray-300 mt-2 tracking-wide">
          Konami Master — you found it.
        </div>
      </div>
    </div>
  );

  // OS mode is the default/primary experience and owns its own boot/login
  // sequence, skipping the Spline loading gate entirely.
  if (isOS) {
    return (
      <>
        <OSApp theme={theme} onSetTheme={setTheme} />
        {konamiToast}
      </>
    );
  }

  // From here on, theme === 'v2' (Game Mode) — the only other destination.
  if (loading) {
    return (
      <>
        <HeroSectionV2 onReady={handleSplineLoad} />
        <LoaderV2 progress={progress} />
        {konamiToast}
      </>
    );
  }

  return (
    <div className="antialiased bg-ink">
      <ParticleField className="fixed inset-0 -z-10" />
      <CursorGlow />
      <NavbarV2 theme={theme} onToggleTheme={toggleTheme} />
      <HeroSectionV2 onReady={handleSplineLoad} />
      <AboutSectionV2 />
      <ProjectsSectionV2 />
      <ContactSectionV2 />
      <FooterV2 />
      {konamiToast}
    </div>
  );
};

export default App;
